import _ from "lodash";
import { useContext } from "react";
import { __RouterContext } from "react-router";
import * as THREE from "three";
import { degToRad } from "three/src/math/MathUtils.js";
import { useStore } from "react-redux";

import { VehicleSettings } from "@skydio/channels/src/update_vehicle_settings_pb";
import { secondsToMilliseconds } from "@skydio/math";
import { VEHICLE_SPEED_LIMITS, VERTICAL_SPEED, GIMBAL_PITCH_RADIANS } from "@skydio/mission-logic";
import { FlightState } from "@skydio/pbtypes/pbtypes/gen/ambassador/flight_state_pb";
import { DeviceType } from "@skydio/pbtypes/pbtypes/gen/vehicle_attributes/device_type_pb";
import { cn } from "@skydio/rivet-ui";

import { dispatchLoader } from "app/(new)/o/[orgId]/(feature-flagged)/(real-time-ops)/_utils/queryStrings";
import { TELEOP_ALERT_CONTAINER_ID } from "components/main/teleop/AlertManagerDisplay/constants";
import { RFD_HUD_COMPASS_ID } from "components/main/teleop/overlay/Compass/constants";
import { GamepadMode } from "hooks/useUserOverrides";
import { boolToNum } from "utils/common";
import { getRouteVehicleId } from "state/router";

import {
  CLOUD_SKILLS,
  GamepadActionInputs,
  GamepadInputType,
  GamepadMovementInputs,
} from "./types";

import type { ValueType } from "@skydio/core";
import type { TransPb } from "@skydio/pbtypes/pbtypes/gen/body/trans_pb";
import type { UnstampedTransf } from "@skydio/pbtypes/pbtypes/gen/vehicle_stats_lite/unstamped_transf_pb";
import type {
  GamepadMovementInputInfo,
  HeadingMovementDirection,
  MouseOrientation,
  MovementDirection,
  PitchMovementDirection,
  PositionMovementDirection,
  TeleopAction,
} from "./types";

// multiplier to determine position/ angular increment
// this is the threshold that determines whether a keypress is a tap or a hold
export const TAP_DURATION = 0.2; // [s]

// timeout to stop sending desired pose commands that come from a single tap. It should only take
// TAP_DURATION for the drone to reach the desired pose but we add a buffer to account for latency
export const DESIRED_POSE_TIMEOUT = TAP_DURATION * 5; // [s]
// timeout to stop sending mouse commands after user moves the mouse (for pointer lock)
export const MOUSE_COMMAND_TIMEOUT = 1.0; // [s]

// Vehicle speed limits from @skydio/mission-logic
export const MAX_VISUAL_NAVIGATION_SPEED = VEHICLE_SPEED_LIMITS.MAX_VISUAL_NAVIGATION;
export const MAX_TRANSLATIONAL_SPEED = VEHICLE_SPEED_LIMITS.MAX_TRANSLATIONAL;
const MAX_NIGHTSENSE_SPEED = VEHICLE_SPEED_LIMITS.MAX_NIGHTSENSE;
export const MAX_TWLIGHT_MODE_SPEED = VEHICLE_SPEED_LIMITS.MAX_TWILIGHT_MODE;

export const KEYBOARD_YAW_PITCH_RATE = Math.PI / 4; // [rad/s]

// movements and look around rates should be a slower when crawling
export const CRAWL_CORRECTION_FACTOR = 0.15;

// how much do we augment the pitch/yaw increment when a key is tapped. this allows us
// fine tuned control of pitch/yaw experience without having to change the TAP_DURATION threshold
export const TAP_PITCH_YAW_CORRECTION_FACTOR = 0.5;

const speedSettingsToMatchControllerFlight = {
  [VehicleSettings.ObstacleMarginMode.DEFAULT_OBSTACLES]: {
    min: 0.5,
    max: MAX_VISUAL_NAVIGATION_SPEED,
    default: 5.0,
    step: 0.5,
  },
  [VehicleSettings.ObstacleMarginMode.REDUCED_OBSTACLES]: {
    min: 0.25,
    max: 8.0,
    default: 2.0,
    step: 0.25,
  },
  [VehicleSettings.ObstacleMarginMode.MINIMAL_OBSTACLES]: {
    min: 0.25,
    max: 8.0,
    default: 2.0,
    step: 0.25,
  },
  // With OA off, we prioritize GPS over VIO which also means we can fly at the vehicle limit
  [VehicleSettings.ObstacleMarginMode.NO_OBSTACLES]: {
    min: 0.5,
    max: MAX_TRANSLATIONAL_SPEED,
    default: 5.0,
    step: 0.5,
  },
};

const nightSenseSpeedSettings = {
  [VehicleSettings.ObstacleMarginMode.DEFAULT_OBSTACLES]: {
    ...speedSettingsToMatchControllerFlight[VehicleSettings.ObstacleMarginMode.DEFAULT_OBSTACLES],
    max: MAX_NIGHTSENSE_SPEED,
  },
  [VehicleSettings.ObstacleMarginMode.REDUCED_OBSTACLES]: {
    ...speedSettingsToMatchControllerFlight[VehicleSettings.ObstacleMarginMode.REDUCED_OBSTACLES],
    max: MAX_NIGHTSENSE_SPEED,
  },
  [VehicleSettings.ObstacleMarginMode.MINIMAL_OBSTACLES]: {
    ...speedSettingsToMatchControllerFlight[VehicleSettings.ObstacleMarginMode.MINIMAL_OBSTACLES],
    max: MAX_NIGHTSENSE_SPEED,
  },
  [VehicleSettings.ObstacleMarginMode.NO_OBSTACLES]: {
    ...speedSettingsToMatchControllerFlight[VehicleSettings.ObstacleMarginMode.NO_OBSTACLES],
    max: MAX_NIGHTSENSE_SPEED,
  },
};

// Speed settings for twilight mode with twilightModeFastFlightFeatureFlagEnabled. With this, the
// user can fly in twilight mode the default obstacle speeds.
const twilightModeFastFlightSpeedSettings = {
  ...speedSettingsToMatchControllerFlight,
  [VehicleSettings.ObstacleMarginMode.NO_OBSTACLES]: {
    ...speedSettingsToMatchControllerFlight[VehicleSettings.ObstacleMarginMode.DEFAULT_OBSTACLES],
    max: MAX_TWLIGHT_MODE_SPEED,
  },
};

export const getSpeedSettings = (nightSenseActive = false, twilightMode = false) => {
  if (nightSenseActive) {
    return nightSenseSpeedSettings;
  } else if (twilightMode) {
    return twilightModeFastFlightSpeedSettings;
  } else {
    return speedSettingsToMatchControllerFlight;
  }
};

// descent / ascent velocity limits
const MAX_DESCENT_SPEED = VERTICAL_SPEED.MAX_DESCENT;
const MAX_RAPID_DESCENT_SPEED = VERTICAL_SPEED.MAX_RAPID_DESCENT;
const MAX_ASCENT_SPEED = VERTICAL_SPEED.MAX_ASCENT;

const MIN_VELOCITY = new THREE.Vector3(
  -MAX_TRANSLATIONAL_SPEED,
  -MAX_TRANSLATIONAL_SPEED,
  -MAX_DESCENT_SPEED
);

// Rapid descent velocity limits
const MIN_RAPID_DESCENT_VELOCITY = new THREE.Vector3(
  -MAX_TRANSLATIONAL_SPEED,
  -MAX_TRANSLATIONAL_SPEED,
  -MAX_RAPID_DESCENT_SPEED
);

const MAX_VELOCITY = new THREE.Vector3(
  MAX_TRANSLATIONAL_SPEED,
  MAX_TRANSLATIONAL_SPEED,
  MAX_ASCENT_SPEED
);

export const clampVelocityToVehicleLimits = (
  velocity: THREE.Vector3,
  isRapidDescentActive = false
) => {
  const minVelocity = isRapidDescentActive ? MIN_RAPID_DESCENT_VELOCITY : MIN_VELOCITY;
  return velocity.clamp(minVelocity, MAX_VELOCITY);
};

// Gimbal pitch limits from @skydio/mission-logic
export const MIN_GIMBAL_PITCH_RADIANS = GIMBAL_PITCH_RADIANS.MIN;
export const MAX_GIMBAL_PITCH_RADIANS = GIMBAL_PITCH_RADIANS.MAX;

// max yaw rate comes from the vehicle
export const MAX_YAW_RATE_RADIANS = degToRad(100); // [rad/s]
// max pitch rate is 145 deg/sec on the vehicle, but we want to be a bit slower in RFD
export const MAX_PITCH_RATE_RADIANS = degToRad(100); // [rad/s]

// click to fly
export const CLICK_TO_FLY_RAY_LENGTH = 10; // [m]

// gamepad
export const GAMEPAD_EVENT_SOURCE = "gamepad" as const;

/** Tag synthetic keyboard events dispatched by the gamepad handler. */
export function tagAsGamepadEvent(event: Event): void {
  Object.assign(event, { source: GAMEPAD_EVENT_SOURCE });
}

/** Check whether a keyboard/mouse event was synthesized by the gamepad handler. */
export function isGamepadEvent(event: Event): boolean {
  return (
    "source" in event && (event as unknown as { source: unknown }).source === GAMEPAD_EVENT_SOURCE
  );
}

export const GAMEPAD_DEADZONE = 0.05; // values below this are ignored

// used to map gamepad analog values to an "expo curve"
export const cubicDeadzoneFunction = (deadZone: number, expoLevel: number) => {
  /*
  Makes the input somewhere between linear and cubic, depending on the expoLevel.

  Args:
    deadZone: value below which the input is considered 0
    expoLevel: interpolates between a linear a cubic curve. Cubic when 0, linear when 1.

  Returns:
    A function with a single argument (number, [-1, 1]) and output (number, [-1, 1])
  */
  return (x: number) => {
    const sgn = x < 0 ? -1 : 1;
    if (Math.abs(x) < deadZone) {
      return 0;
    }
    const scaledX = (sgn * (Math.abs(x) - deadZone)) / (1 - deadZone);
    return expoLevel * scaledX + (1 - expoLevel) * Math.pow(scaledX, 3);
  };
};

// used to reduce pitch/yaw rate when zoomed in
// when 0 speed is not affected by zoom, when 1 then speed correction factor is 1/(zoomLevel)
const ZOOM_SENSITIVITY = 0.3;
export const getZoomCorrection = (zoomLevel: number) => {
  return 1 / (1 + ZOOM_SENSITIVITY * (zoomLevel - 1));
};

// makes it easy to iterate over the enum while also getting information about its type
export function* supportedGamepadMovementInputs(): IterableIterator<GamepadMovementInputInfo> {
  yield { input: GamepadMovementInputs.GAMEPAD_AXES_LEFT_STICK_X, type: GamepadInputType.AXIS };
  yield { input: GamepadMovementInputs.GAMEPAD_AXES_LEFT_STICK_Y, type: GamepadInputType.AXIS };
  yield { input: GamepadMovementInputs.GAMEPAD_AXES_RIGHT_STICK_X, type: GamepadInputType.AXIS };
  yield { input: GamepadMovementInputs.GAMEPAD_AXES_RIGHT_STICK_Y, type: GamepadInputType.AXIS };
  yield { input: GamepadMovementInputs.GAMEPAD_BUTTON_LT, type: GamepadInputType.BUTTON };
  yield { input: GamepadMovementInputs.GAMEPAD_BUTTON_RT, type: GamepadInputType.BUTTON };
}

// returns whether the gamepad mapping is supported by teleop
export const isGamepadSupported = (mapping: GamepadMappingType) => {
  // for now, we only support the standard mapping
  return mapping === "standard";
};

export const getZeroMovementInput = (keyboard: boolean): Record<MovementDirection, number> => {
  // for keyboard inputs we store the time the key was pressed so intitialize to -1. For gamepad, we
  // store the analog value so initialize to 0
  const defaultValue = keyboard ? -1 : 0;
  return {
    forwards: defaultValue,
    backwards: defaultValue,
    left: defaultValue,
    right: defaultValue,
    up: defaultValue,
    down: defaultValue,
    yawLeft: defaultValue,
    yawRight: defaultValue,
    gimbalUp: defaultValue,
    gimbalDown: defaultValue,
  };
};

// mapping that is robust to whether the input is an axis or a button
// first value in the array is the negative direction, second is the positive direction
// (note that for the right and left stick, positive y is down)
export const gamepadModeMappings: Record<
  GamepadMode,
  Record<GamepadMovementInputs, Array<MovementDirection>>
> = {
  [GamepadMode.MODE_1]: {
    [GamepadMovementInputs.GAMEPAD_AXES_LEFT_STICK_X]: ["yawLeft", "yawRight"],
    [GamepadMovementInputs.GAMEPAD_AXES_LEFT_STICK_Y]: ["forwards", "backwards"],
    [GamepadMovementInputs.GAMEPAD_AXES_RIGHT_STICK_X]: ["left", "right"],
    [GamepadMovementInputs.GAMEPAD_AXES_RIGHT_STICK_Y]: ["up", "down"],
    [GamepadMovementInputs.GAMEPAD_BUTTON_LT]: ["gimbalDown"],
    [GamepadMovementInputs.GAMEPAD_BUTTON_RT]: ["gimbalUp"],
  },
  [GamepadMode.MODE_2]: {
    [GamepadMovementInputs.GAMEPAD_AXES_LEFT_STICK_X]: ["yawLeft", "yawRight"],
    [GamepadMovementInputs.GAMEPAD_AXES_LEFT_STICK_Y]: ["up", "down"],
    [GamepadMovementInputs.GAMEPAD_AXES_RIGHT_STICK_X]: ["left", "right"],
    [GamepadMovementInputs.GAMEPAD_AXES_RIGHT_STICK_Y]: ["forwards", "backwards"],
    [GamepadMovementInputs.GAMEPAD_BUTTON_LT]: ["gimbalDown"],
    [GamepadMovementInputs.GAMEPAD_BUTTON_RT]: ["gimbalUp"],
  },
  [GamepadMode.MODE_3]: {
    [GamepadMovementInputs.GAMEPAD_AXES_LEFT_STICK_X]: ["left", "right"],
    [GamepadMovementInputs.GAMEPAD_AXES_LEFT_STICK_Y]: ["forwards", "backwards"],
    [GamepadMovementInputs.GAMEPAD_AXES_RIGHT_STICK_X]: ["yawLeft", "yawRight"],
    [GamepadMovementInputs.GAMEPAD_AXES_RIGHT_STICK_Y]: ["up", "down"],
    [GamepadMovementInputs.GAMEPAD_BUTTON_LT]: ["gimbalDown"],
    [GamepadMovementInputs.GAMEPAD_BUTTON_RT]: ["gimbalUp"],
  },
  [GamepadMode.VIDEO_GAME_MODE]: {
    [GamepadMovementInputs.GAMEPAD_AXES_LEFT_STICK_X]: ["left", "right"],
    [GamepadMovementInputs.GAMEPAD_AXES_LEFT_STICK_Y]: ["forwards", "backwards"],
    [GamepadMovementInputs.GAMEPAD_AXES_RIGHT_STICK_X]: ["yawLeft", "yawRight"],
    [GamepadMovementInputs.GAMEPAD_AXES_RIGHT_STICK_Y]: ["gimbalUp", "gimbalDown"],
    [GamepadMovementInputs.GAMEPAD_BUTTON_LT]: ["down"],
    [GamepadMovementInputs.GAMEPAD_BUTTON_RT]: ["up"],
  },
};

// makes it easy to iterate over the enum
export function* supportedGamepadActionInputs(): IterableIterator<GamepadActionInputs> {
  yield GamepadActionInputs.GAMEPAD_BUTTON_DPAD_UP;
  yield GamepadActionInputs.GAMEPAD_BUTTON_DPAD_DOWN;
  yield GamepadActionInputs.GAMEPAD_BUTTON_DPAD_LEFT;
  yield GamepadActionInputs.GAMEPAD_BUTTON_DPAD_RIGHT;
  yield GamepadActionInputs.GAMEPAD_BUTTON_Y;
  yield GamepadActionInputs.GAMEPAD_BUTTON_X;
  yield GamepadActionInputs.GAMEPAD_BUTTON_RB;
  yield GamepadActionInputs.GAMEPAD_BUTTON_LB;
  yield GamepadActionInputs.GAMEPAD_BUTTON_A;
  yield GamepadActionInputs.GAMEPAD_BUTTON_B;
}
// mapping for teleop actions
export const gamepadActionMappings: Record<GamepadActionInputs, TeleopAction> = {
  [GamepadActionInputs.GAMEPAD_BUTTON_DPAD_UP]: "zoomIn",
  [GamepadActionInputs.GAMEPAD_BUTTON_DPAD_DOWN]: "zoomOut",
  [GamepadActionInputs.GAMEPAD_BUTTON_DPAD_LEFT]: "workflowPrevious",
  [GamepadActionInputs.GAMEPAD_BUTTON_DPAD_RIGHT]: "workflowNext",
  [GamepadActionInputs.GAMEPAD_BUTTON_Y]: "launchOrReturnToDock",
  [GamepadActionInputs.GAMEPAD_BUTTON_X]: "takePhoto",
  [GamepadActionInputs.GAMEPAD_BUTTON_RB]: "crawl",
  [GamepadActionInputs.GAMEPAD_BUTTON_LB]: "boost",
  [GamepadActionInputs.GAMEPAD_BUTTON_A]: "selectSubject",
  [GamepadActionInputs.GAMEPAD_BUTTON_B]: "cancel",
};

export const getFlightStateName = (flightState: ValueType<FlightState.EnumMap>) =>
  _.findKey(FlightState.Enum, o => o === flightState);

export const obstacleSafetyToObstacleMode = (obstacleSafety: number) => {
  // Nominally these values should map as such:
  // -1 -> no obstacles
  // -0.5 -> minimal avoidance
  // 0 -> close avoidance
  // 1 -> standard avoidance
  // The following checks are implemented continuously since we'll get intermediate float values
  // while the state is transitioning
  if (_.isNumber(obstacleSafety)) {
    if (obstacleSafety <= -1.0) {
      return VehicleSettings.ObstacleMarginMode.NO_OBSTACLES;
    }
    if (obstacleSafety < 0.0) {
      return VehicleSettings.ObstacleMarginMode.MINIMAL_OBSTACLES;
    }
    if (obstacleSafety < 1.0) {
      return VehicleSettings.ObstacleMarginMode.REDUCED_OBSTACLES;
    }
  }
  return VehicleSettings.ObstacleMarginMode.DEFAULT_OBSTACLES;
};

/**
 * Passed in a timestamp that the key was pressed, this function will return whether or not
 *   the key is considered "held" as opposed to "tapped" or "not pressed".
 * @param keyPressedTimestamp when the key was pressed
 * @returns boolean whether the key is held or not
 */
export const isKeyHeld = (keyPressedTimestamp: number) =>
  keyPressedTimestamp > 0 &&
  performance.now() - keyPressedTimestamp > secondsToMilliseconds(TAP_DURATION);

/**
 * Returns a normalized movement vector based on position inputs
 */
const movementVector = (
  forwards: boolean,
  backwards: boolean,
  left: boolean,
  right: boolean,
  up: boolean,
  down: boolean
) =>
  new THREE.Vector3(
    boolToNum(forwards) - boolToNum(backwards), // x
    boolToNum(left) - boolToNum(right), // y
    boolToNum(up) - boolToNum(down) // z
  ).normalize();

/**
 * Returns a normalized movement vector based on the keyboard inputs
 */
export const getKeyboardMovementVector = (inputs: Record<MovementDirection, number>) =>
  movementVector(
    inputs.forwards > 0,
    inputs.backwards > 0,
    inputs.left > 0,
    inputs.right > 0,
    inputs.up > 0,
    inputs.down > 0
  );

export const getGamepadMovementVector = (inputs: Record<MovementDirection, number>) =>
  new THREE.Vector3(
    inputs.forwards - inputs.backwards, // x
    inputs.left - inputs.right, // y
    inputs.up - inputs.down // z
  );

// Rdf = right-down-forward, Flu = forward-left-up
const kRdf_R_Flu = new THREE.Quaternion(0.5, -0.5, 0.5, 0.5);
const kFlu_R_Rdf = kRdf_R_Flu.clone().invert();

export const convertFluToRdf = (a_R_b: THREE.Quaternion) => {
  // rdf_R_rdf = rdf_R_flu * a_(flu)_R_b_(flu) * flu_R_rdf
  return kRdf_R_Flu.clone().multiply(a_R_b.clone()).multiply(kFlu_R_Rdf);
};

export const convertRdfToFlu = (a_R_b: THREE.Quaternion) => {
  // flu_R_flu = flu_R_rdf * a_(rdf)_R_b_(rdf) * rdf_R_flu
  return kFlu_R_Rdf.clone().multiply(a_R_b.clone()).multiply(kRdf_R_Flu);
};

// Returns nav_T_command with nav in FLU and command in RDF
export const calculateNavTCommand = (
  nav_T_imu?: THREE.Object3D,
  imu_T_cam_rdf?: THREE.Object3D,
  imu_R_command_rdf?: THREE.Quaternion,
  nav_t_imu_stale?: boolean
) => {
  if (!imu_T_cam_rdf || !nav_T_imu || nav_t_imu_stale) {
    return undefined;
  }
  const matrixTransform = new THREE.Matrix4();
  // if we have imu_R_command_rdf, then return compute nav_T_command. Otherwise, compute nav_T_cam
  // and assume the command is relative to the camera.
  if (imu_R_command_rdf) {
    matrixTransform.multiplyMatrices(
      nav_T_imu.matrix,
      new THREE.Matrix4().makeRotationFromQuaternion(imu_R_command_rdf)
    );
  } else {
    matrixTransform.multiplyMatrices(nav_T_imu.matrix, imu_T_cam_rdf.matrix);
  }
  const nav_T_command = new THREE.Object3D();
  nav_T_command.applyMatrix4(matrixTransform);
  return nav_T_command;
};

const GIMBAL_ORIENTATION_MUTABLE = new THREE.Quaternion();
const GIMBAL_Z_MUTABLE = new THREE.Vector3();
const VEHICLE_X_MUTABLE = new THREE.Vector3();
const PITCH_THRESHOLD_TO_USE_GIMBAL_Z_FOR_HEADING = degToRad(85);
const YPR_MUTABLE = new THREE.Euler();

export const calculateNavHeadingPitchFromGimbalAndVehicleOrientation = (
  gimbalOrientation?: THREE.Quaternion,
  vehicleOrientation?: THREE.Quaternion
): MouseOrientation | undefined => {
  if (!gimbalOrientation) {
    // TODO(Saumya): Maybe return undefined if vehicle orientation is also undefined and we are pitched near straight up.
    return undefined;
  }
  // gimbal is in right-down-front (RDF) so we need to convert to forward-left-up (FLU)
  const orientation = GIMBAL_ORIENTATION_MUTABLE.copy(gimbalOrientation).multiply(kRdf_R_Flu);

  YPR_MUTABLE.setFromQuaternion(orientation, "ZYX");

  // This is in forward-left-up (FLU) and therefore, pitch is the rotation about the y axis and
  // heading is the rotation about the z axis
  const pitch = YPR_MUTABLE.y;
  let heading = YPR_MUTABLE.z;

  // If the absolute value of the pitch is close to vertical then we need to use the gimbal z-axis
  // (in FLU) to determine the heading to avoid issues with gimbal lock with the gimbal x-axis
  if (Math.abs(pitch) > PITCH_THRESHOLD_TO_USE_GIMBAL_Z_FOR_HEADING && vehicleOrientation) {
    // Else if we have vehicle orientation, use it to determine the heading direction when pitching up
    // (and potentially gimbal flipped).
    GIMBAL_Z_MUTABLE.set(0, 0, 1);
    GIMBAL_Z_MUTABLE.applyQuaternion(orientation);

    VEHICLE_X_MUTABLE.set(1, 0, 0);
    VEHICLE_X_MUTABLE.applyQuaternion(vehicleOrientation);
    // Take dot product between gimbal Z and vehicle Z:
    const dotProduct = GIMBAL_Z_MUTABLE.dot(VEHICLE_X_MUTABLE);
    if (dotProduct < 0) {
      GIMBAL_Z_MUTABLE.multiplyScalar(-1);
    }
    heading = Math.atan2(GIMBAL_Z_MUTABLE.y, GIMBAL_Z_MUTABLE.x);
  } else if (pitch > PITCH_THRESHOLD_TO_USE_GIMBAL_Z_FOR_HEADING) {
    // Gimbal is pitching down and near gimbal lock, use gimbal Z mutable. Vehicle orientation isn't needed here because
    // gimbal only flips to look up.
    GIMBAL_Z_MUTABLE.set(0, 0, 1);
    GIMBAL_Z_MUTABLE.applyQuaternion(orientation);
    heading = Math.atan2(GIMBAL_Z_MUTABLE.y, GIMBAL_Z_MUTABLE.x);
  } else if (pitch < -1 * PITCH_THRESHOLD_TO_USE_GIMBAL_Z_FOR_HEADING) {
    // Gimbal is pitching up and near gimbal lock, use gimbal Z mutable. This assumes that the gimbal flip is
    // already accounted for and transform is for the rotated stream.
    GIMBAL_Z_MUTABLE.set(0, 0, 1);
    GIMBAL_Z_MUTABLE.applyQuaternion(orientation);
    GIMBAL_Z_MUTABLE.multiplyScalar(-1);
    heading = Math.atan2(GIMBAL_Z_MUTABLE.y, GIMBAL_Z_MUTABLE.x);
  }
  return { heading, pitch };
};

export const calculateNavHeadingPitch = (
  nav_T_cam?: THREE.Object3D,
  vehicleOrientation?: THREE.Quaternion
): MouseOrientation | undefined => {
  if (!nav_T_cam) {
    return undefined;
  }
  return calculateNavHeadingPitchFromGimbalAndVehicleOrientation(
    nav_T_cam.quaternion,
    vehicleOrientation
  );
};

export const calculateImuTCamHeadingPitch = (imu_T_cam_rdf?: THREE.Object3D) => {
  if (!imu_T_cam_rdf) {
    return undefined;
  }
  // Both imu and cam are in RDF so we need to convert to FLU
  const orientation = convertRdfToFlu(imu_T_cam_rdf.quaternion);
  const rpy = new THREE.Euler().setFromQuaternion(orientation, "ZYX");
  const headingPitch: MouseOrientation = {
    heading: rpy.z,
    pitch: rpy.y,
  };
  return headingPitch;
};

/**
 * Calculate the pitch rate based on all relevant factors including:
 * - The direction of the pitch (gimbal up or down)
 * - The zoom correction factor
 * - The crawl correction factor
 * @param pitchDirection gimbalUp or gimbalDown. if null, returns 0
 * @param zoomCorrectionFactor
 * @param crawlCorrectionFactor
 * @returns a pitch rate in rads/sec
 */
export const augmentedKeyboardPitchRate = (
  pitchDirection: PitchMovementDirection | null,
  zoomCorrectionFactor: number,
  isCrawling: boolean
) => {
  if (!pitchDirection) return 0;

  return (
    KEYBOARD_YAW_PITCH_RATE *
    zoomCorrectionFactor *
    (isCrawling ? CRAWL_CORRECTION_FACTOR : 1) *
    (pitchDirection === "gimbalUp" ? -1 : 1)
  );
};

/**
 * Calculate the heading rate based on all relevant factors including:
 * - The direction of the heading (yaw left or right)
 * - The zoom correction factor
 * - The crawl correction factor
 * @param headingDirection yawLeft or yawRight. if null, returns 0
 * @param zoomCorrectionFactor
 * @param crawlCorrectionFactor
 * @returns a heading rate in rads/sec
 */
export const augmentedKeyboardHeadingRate = (
  headingDirection: HeadingMovementDirection | null,
  zoomCorrectionFactor: number,
  isCrawling: boolean
) => {
  if (!headingDirection) return 0;

  return (
    KEYBOARD_YAW_PITCH_RATE *
    zoomCorrectionFactor *
    (isCrawling ? CRAWL_CORRECTION_FACTOR : 1) *
    (headingDirection === "yawRight" ? -1 : 1)
  );
};

/**
 * Calculate the new desired position on tap
 * @param currentPosition
 * @param movementDirection which direction was tapped
 * @param navHeading which direction the drone is currently facing
 * @returns a new vector representing the new desired position
 */
export function calculateNewPositionOnTap(
  currentPosition: THREE.Vector3,
  movementDirection: PositionMovementDirection,
  navHeading: number,
  nominalSpeed: number
): THREE.Vector3 {
  const rotatedMovementVector = movementVector(
    movementDirection === "forwards",
    movementDirection === "backwards",
    movementDirection === "left",
    movementDirection === "right",
    movementDirection === "up",
    movementDirection === "down"
  )
    .applyAxisAngle(new THREE.Vector3(0, 0, 1), navHeading)
    .multiplyScalar(nominalSpeed * TAP_DURATION);

  return currentPosition.clone().add(rotatedMovementVector);
}

export const getTeleopAntMessageClassName = (): string => {
  // We don't have access to the feature flags in the pilot state slice, so this is the best heuristic we have
  const isAlertManagerEnabled = document.getElementById(TELEOP_ALERT_CONTAINER_ID) != null;
  const isRfdHudCompassEnabled = document.getElementById(RFD_HUD_COMPASS_ID) != null;

  return cn({
    "mt-16": isAlertManagerEnabled && isRfdHudCompassEnabled,
    "mt-20": !isAlertManagerEnabled && !isRfdHudCompassEnabled,
  });
};

export const toTransPb = (value: UnstampedTransf.AsObject, utime: number): TransPb.AsObject => {
  return {
    utime,
    orientation: value.orientation,
    position: value.position,
  };
};

// Support starting missions from the ground (e.g fly to point)
const SUPPORTED_SKILLS_ON_GROUND = [
  CLOUD_SKILLS.MANUAL,
  CLOUD_SKILLS.WAYPOINTS,
  CLOUD_SKILLS.MISSION_PLANNER,
];

export const isSkillSupported = (skillKey: string | undefined, isAirborne: boolean) => {
  if (!skillKey) {
    return undefined;
  }
  const groundSupportedSkill = SUPPORTED_SKILLS_ON_GROUND.includes(skillKey as CLOUD_SKILLS);

  const cloudSupportedSkill = Object.values(CLOUD_SKILLS).includes(skillKey as CLOUD_SKILLS);

  return isAirborne ? cloudSupportedSkill : groundSupportedSkill;
};

/**
 * Get the vehicle ID from the URL or query param
 * @returns The vehicle ID.
 */
export function useVehicleId() {
  const routerContext = useContext(__RouterContext);
  const routeVehicleId = (routerContext?.match as { params?: { vehicleId?: string } } | undefined)
    ?.params?.vehicleId;
  const parsedParams =
    typeof window !== "undefined" ? dispatchLoader(window.location.search) : dispatchLoader("");

  const reduxStore = useStore();

  return routeVehicleId ?? parsedParams.activeVehicle ?? getRouteVehicleId(reduxStore.getState());
}

export const MOBILE_SL_PEER_TYPES = new Set<ValueType<DeviceType.EnumMap>>([
  DeviceType.Enum.C18,
  DeviceType.Enum.C38,
]);
